import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Sparkles, TrendingUp, TrendingDown, Users, AlertCircle, Target, Headphones, BookOpen, UserCheck, GraduationCap, Mail, AlertTriangle, Send } from 'lucide-react';
import AssignPracticeModal from './AssignPracticeModal';

/**
 * Reusable diagnostic + AI action plan view for teacher/head-teacher review.
 *
 * Props:
 *   diagnosticUrl: GET endpoint that returns the diagnostic payload
 *   actionPlanUrl: POST endpoint that generates the AI action plan
 *   diagnostic:    optional pre-loaded data (skips initial fetch)
 *   onShowDetails: optional callback to switch to a question-by-question view
 */
const TIER_COLORS = {
  critical: 'bg-red-100 border-red-300 text-red-800',
  weak: 'bg-orange-100 border-orange-300 text-orange-800',
  average: 'bg-yellow-100 border-yellow-300 text-yellow-800',
  strong: 'bg-green-100 border-green-300 text-green-800',
};
const TIER_LABEL = {
  critical: 'Critical',
  weak: 'Weak',
  average: 'Average',
  strong: 'Strong',
};
const CHANNEL_ICON = {
  class: GraduationCap,
  'one-on-one': UserCheck,
  audio: Headphones,
  practice: Target,
  parent: Mail,
};

const StudentInsights = ({ diagnosticUrl, actionPlanUrl, onShowDetails }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [autoTried, setAutoTried] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // which action item is being assigned

  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, [diagnosticUrl]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(diagnosticUrl);
      setData(res.data);
      // Auto-generate AI action plan on first view if not cached and student has weak items
      const hasWeakness = (res.data?.weak_concepts?.length || 0) > 0
        || (res.data?.topic_breakdown || []).some(t => t.tier === 'critical' || t.tier === 'weak');
      if (!res.data?.ai_action_plan && hasWeakness && actionPlanUrl && !autoTried) {
        setAutoTried(true);
        generatePlan(true);
      }
    } catch (err) {
      toast.error('Failed to load diagnostic');
    } finally {
      setLoading(false);
    }
  };

  const generatePlan = async (silent = false) => {
    try {
      setGenerating(true);
      const res = await axios.post(actionPlanUrl);
      setData(prev => ({ ...prev, ai_action_plan: res.data.ai_action_plan, ai_action_plan_generated_at: res.data.generated_at }));
      if (!silent) toast.success('AI action plan generated');
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <div className="bg-white rounded-xl p-8 text-center"><Loader2 className="animate-spin mx-auto text-blue-500" /></div>;
  if (!data) return null;

  const pct = data.percentage ?? (data.total_marks ? Math.round((data.score / data.total_marks) * 100) : 0);
  const aboveAvg = data.class_stats?.class_avg_percent != null && pct > data.class_stats.class_avg_percent;
  const plan = data.ai_action_plan;
  const isGroupMode = data.persistent_gaps !== undefined; // aggregated-across-attempts mode

  return (
    <div className="space-y-4">
      {/* Header — for group mode the parent renders its own hero with attempts/best/latest, so we suppress this score hero. */}
      {!isGroupMode && (
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-xl p-4 md:p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wide opacity-80">Student Diagnostic</p>
              <h3 className="text-lg md:text-xl font-bold">{data.student_name}</h3>
            </div>
            <div className="text-right">
              <p className="text-3xl md:text-4xl font-bold">{data.score} / {data.total_marks}</p>
              <p className="text-sm opacity-90">{pct}%</p>
            </div>
          </div>
          {data.class_stats?.class_avg_percent != null && data.class_stats.class_size > 1 && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <Users size={14} className="opacity-80" />
              <span>
                Class avg: <strong>{data.class_stats.class_avg_percent}%</strong> ·
                Position <strong>{data.class_stats.student_position}/{data.class_stats.class_size}</strong> ·
                {aboveAvg ? <span className="ml-1"><TrendingUp size={14} className="inline" /> Above average</span> : <span className="ml-1"><TrendingDown size={14} className="inline" /> Below average</span>}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Topic Breakdown */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-5">
        <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><BookOpen size={18} className="text-blue-600" /> Performance by Chapter / Topic</h4>
        {data.topic_breakdown?.length ? (
          <div className="space-y-2">
            {data.topic_breakdown.map((t, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-lg border bg-gray-50">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{t.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden max-w-xs">
                      <div
                        className={`h-full rounded-full ${
                          t.tier === 'strong' ? 'bg-green-500'
                          : t.tier === 'average' ? 'bg-yellow-500'
                          : t.tier === 'weak' ? 'bg-orange-500'
                          : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(t.accuracy_percent, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-600 shrink-0">{t.marks_earned}/{t.marks_total} ({t.accuracy_percent}%)</span>
                  </div>
                </div>
                <Badge variant="outline" className={`shrink-0 ${TIER_COLORS[t.tier]}`}>{TIER_LABEL[t.tier]}</Badge>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No topic data available.</p>
        )}
      </div>

      {/* Persistent Gaps — shown when this view aggregates multiple attempts */}
      {data.persistent_gaps?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-5 border-l-4 border-red-500" data-testid="persistent-gaps-section">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-600" />
            Persistent Gaps — across {data.attempt_count || '—'} attempts
            <Badge variant="outline" className="ml-1 text-xs bg-red-50 border-red-300 text-red-700">
              {data.persistent_gaps.filter(p => p.is_persistent).length} recurring · {data.persistent_gaps.length} total
            </Badge>
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            Concepts the student missed in <strong>multiple</strong> attempts are persistent gaps — they need teaching, not just more practice.
          </p>
          <div className="space-y-2.5">
            {data.persistent_gaps.map((w, i) => (
              <div
                key={i}
                className={`rounded-lg border p-3 ${w.is_persistent ? 'border-red-300 bg-red-50/60' : 'border-orange-200 bg-orange-50/40'}`}
                data-testid={`persistent-gap-${i}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-red-700 bg-red-100 px-2 py-0.5 rounded">
                      {w.topic}
                    </span>
                    {w.is_persistent ? (
                      <span className="text-[10px] font-bold text-red-700 bg-red-200 px-2 py-0.5 rounded uppercase">
                        Missed {w.missed_in}× of {w.total_attempts} attempts
                      </span>
                    ) : (
                      <span className="text-[10px] text-orange-700 bg-orange-100 px-2 py-0.5 rounded uppercase">
                        One-off miss
                      </span>
                    )}
                  </div>
                </div>
                {w.question && (
                  <p className="text-sm text-gray-800 font-medium mb-1.5 line-clamp-3">Q: {w.question}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="bg-white rounded p-2 border border-red-100">
                    <p className="text-[10px] uppercase font-semibold text-red-600 mb-0.5">Student answered</p>
                    <p className="text-gray-800 break-words">{w.student_answer || '(no answer)'}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-green-200">
                    <p className="text-[10px] uppercase font-semibold text-green-700 mb-0.5">Correct answer</p>
                    <p className="text-gray-800 break-words">{w.correct_answer || '—'}</p>
                  </div>
                </div>
                {w.explanation && (
                  <div className="mt-2 bg-blue-50 border border-blue-200 rounded p-2">
                    <p className="text-[10px] uppercase font-semibold text-blue-700 mb-0.5">Why this is the right answer</p>
                    <p className="text-xs text-gray-700">{w.explanation}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Specific Weak Concepts — single-attempt view (hidden when persistent_gaps is present) */}
      {!data.persistent_gaps && data.weak_concepts?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-5 border-l-4 border-red-400" data-testid="weak-concepts-section">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-500" />
            Where {data.student_name?.split(' ')[0] || 'Student'} is Weak
            <Badge variant="outline" className="ml-1 text-xs bg-red-50 border-red-300 text-red-700">
              {data.weak_concepts.length} {data.weak_concepts.length === 1 ? 'concept' : 'concepts'}
            </Badge>
          </h4>
          <p className="text-xs text-gray-500 mb-3">These are the exact questions/concepts the student got wrong — focus your teaching here.</p>
          <div className="space-y-2.5">
            {data.weak_concepts.map((w, i) => (
              <div key={i} className="rounded-lg border border-red-200 bg-red-50/40 p-3" data-testid={`weak-concept-${i}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-red-700 bg-red-100 px-2 py-0.5 rounded">
                      {w.concept}
                    </span>
                    {w.type && <span className="text-[10px] text-gray-500 uppercase">{w.type.replace('_', ' ')}</span>}
                  </div>
                  <span className="text-xs font-semibold text-red-700">{w.marks_earned}/{w.marks_total} marks</span>
                </div>
                {w.question && (
                  <p className="text-sm text-gray-800 font-medium mb-1.5 line-clamp-3">Q: {w.question}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="bg-white rounded p-2 border border-red-100">
                    <p className="text-[10px] uppercase font-semibold text-red-600 mb-0.5">Student answered</p>
                    <p className="text-gray-800 break-words">{w.student_answer || '(no answer)'}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-green-200">
                    <p className="text-[10px] uppercase font-semibold text-green-700 mb-0.5">Correct answer</p>
                    <p className="text-gray-800 break-words">{w.correct_answer || '—'}</p>
                  </div>
                </div>
                {w.explanation && (
                  <div className="mt-2 bg-blue-50 border border-blue-200 rounded p-2">
                    <p className="text-[10px] uppercase font-semibold text-blue-700 mb-0.5">Why this is the right answer</p>
                    <p className="text-xs text-gray-700">{w.explanation}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Question Type Pattern — intentionally removed.
          Teachers asked for concept/chapter-level feedback, not "MCQ %" / "short answer %"
          stats which feel artificial. The data is still computed server-side and is fed
          into the AI Action Plan prompt as one (de-emphasised) signal. */}

      {/* Trend */}
      {data.trend?.length > 1 && (
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-5">
          <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><TrendingUp size={18} className="text-green-600" /> Recent Performance Trend</h4>
          <div className="flex items-end gap-2 overflow-x-auto">
            {data.trend.slice(-6).map((t, i) => (
              <div key={i} className="flex flex-col items-center text-center min-w-[60px]">
                <div
                  className={`w-12 rounded-t ${
                    t.percentage >= 70 ? 'bg-green-500'
                    : t.percentage >= 40 ? 'bg-yellow-500'
                    : 'bg-red-500'
                  }`}
                  style={{ height: `${Math.max(20, t.percentage * 1.2)}px` }}
                />
                <p className="text-xs font-medium mt-1">{t.percentage}%</p>
                <p className="text-[10px] text-gray-500">
                  {t.submitted_at ? new Date(t.submitted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Action Plan */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-sm p-4 md:p-5 border border-indigo-200">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            <Sparkles size={18} className="text-indigo-600" /> AI Action Plan
          </h4>
          {plan ? (
            <Button size="sm" variant="outline" onClick={generatePlan} disabled={generating} data-testid="regenerate-action-plan">
              {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
              Regenerate
            </Button>
          ) : (
            <Button size="sm" onClick={generatePlan} disabled={generating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="generate-action-plan">
              {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
              Generate Plan
            </Button>
          )}
        </div>

        {!plan ? (
          generating ? (
            <div className="flex items-center gap-2 text-sm text-indigo-700">
              <Loader2 size={16} className="animate-spin" />
              Generating personalized teaching plan for {data.student_name?.split(' ')[0] || 'this student'}…
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              Click <strong>Generate Plan</strong> to get an AI-powered diagnosis and 3-5 concrete teaching actions tailored to this student's weak areas.
            </p>
          )
        ) : (
          <div className="space-y-3">
            <div className="bg-white rounded-lg p-3 border border-indigo-200">
              <p className="text-xs font-semibold text-indigo-700 mb-1 uppercase tracking-wide">Diagnosis</p>
              <p className="text-sm text-gray-800">{plan.diagnosis}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-indigo-700 mb-2 uppercase tracking-wide">Recommended Actions</p>
              <div className="space-y-2">
                {(plan.action_plan || []).map((a, i) => {
                  const Icon = CHANNEL_ICON[a.channel] || Sparkles;
                  const isActionable = a.channel === 'practice';
                  return (
                    <div key={i} className="bg-white rounded-lg p-3 border border-indigo-200 flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                        <Icon size={16} className="text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{a.action}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{a.why}</p>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {a.channel && <Badge variant="outline" className="text-xs capitalize">{a.channel}</Badge>}
                          {isActionable && (
                            <button
                              onClick={() => setAssignFor(a)}
                              className="text-xs font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-full px-3 py-1 flex items-center gap-1"
                              data-testid={`assign-practice-${i}`}
                            >
                              <Send size={11} /> Assign to students
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {data.ai_action_plan_generated_at && (
              <p className="text-xs text-gray-500 italic">
                Generated {new Date(data.ai_action_plan_generated_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Show details toggle */}
      {onShowDetails && (
        <div className="text-center">
          <Button variant="ghost" onClick={onShowDetails} data-testid="show-details-btn">
            View question-by-question breakdown →
          </Button>
        </div>
      )}

      {/* Assign Practice modal — opens when a "practice" action item is clicked */}
      <AssignPracticeModal
        open={Boolean(assignFor)}
        onClose={() => setAssignFor(null)}
        sourceStudent={{ student_id: data?.student?.id, name: data?.student?.name }}
        classLevel={data?.student?.class_level || data?.class_level}
        subject={data?.subject || data?.test?.subject}
        chapterName={data?.chapter || data?.test?.title}
        actionTitle={assignFor?.action}
      />
    </div>
  );
};

export default StudentInsights;
